import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { MIcon } from '../../lib/micon';
import { apiFetch, ApiError, zeigeApiFehler } from '../../lib/api';
import { toast } from '../../lib/toast';
import { confirm } from '../../lib/confirm';
import { openAusflugDetail, oeffneKlassenbuch } from '../../lib/nav';
import { ladeFehlzeitenGrenzen } from '../../lib/utils';
import { schuljahrFenster } from '../../lib/schuljahr';
import { LdSheet } from '../../components/ui/LdSheet';
import { LdFeld, LdTextfeld, LdDatum, LdZahl, LdUmschalter, LdFensterFuss } from '../../components/ld/LdFenster';
import './FehlzeitenEintraege.css';
import { note6Rueckfrage as note6RueckfrageGemeinsam, BestaetigteNote6 } from './note6-rueckfrage';

// Welle U3b: Fehlzeiten im Profil — Kennzahlen, Eintragsliste (laufendes Schuljahr),
// Bearbeiten-Sheet, Schnellknopf "Attest nachgereicht" und Note-6-Rückfrage.
// Datenquelle ist GET /api/fehlzeiten?schueler_id (U3a/U3c); Änderungen über PUT/DELETE.

type Quelle = 'manuell' | 'diktat' | 'klassenbuch' | 'ausflug' | 'mail' | null;
export interface FzEintrag {
  id: number; schueler_id: number; datum: string;
  stunden: number | null; tage: number | null;
  entschuldigt: 0 | 1; attest: 0 | 1; grund: string | null;
  quelle: Quelle; quelle_text: string; bearbeitbar: boolean;
  gesperrte_felder: string[]; loeschen_hinweis: string | null;
  klassenbuch_id: number | null; kb_stunde: number | null; kb_fach: string | null;
  ausflug_titel: string | null; ausflug_termin_id: number | null;
  letzte_aenderung: { am: string; vorher: Record<string, any>; nachberechnung: boolean } | null;
}
type Status = 'unentsch' | 'entsch' | 'attest';
type Filter = 'alle' | Status;

const statusVon = (e: { entschuldigt: number; attest: number }): Status => e.attest ? 'attest' : e.entschuldigt ? 'entsch' : 'unentsch';
const STATUS_LABEL: Record<Status, string> = { unentsch: 'Unentschuldigt', entsch: 'Entschuldigt', attest: 'Mit Attest' };
const STATUS_KURZ: Record<Status, string> = { unentsch: 'Unentsch.', entsch: 'Entsch.', attest: 'Attest' };
const STATUS_ICON: Record<Status, string> = { unentsch: 'radio_button_unchecked', entsch: 'check', attest: 'medical_information' };
const WT = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
const MONATE = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
const MON_KURZ = ['Jan.', 'Feb.', 'März', 'Apr.', 'Mai', 'Juni', 'Juli', 'Aug.', 'Sep.', 'Okt.', 'Nov.', 'Dez.'];

const d = (iso: string) => new Date(iso.slice(0, 10) + 'T12:00:00');
const ttmm = (iso: string) => { const x = d(iso); return `${String(x.getDate()).padStart(2, '0')}.${String(x.getMonth() + 1).padStart(2, '0')}.`; };
// audit_log.created_at ist UTC ('YYYY-MM-DD HH:MM:SS') → als Ortsdatum anzeigen
const ttmmUtc = (ts: string) => { const x = new Date(ts.replace(' ', 'T') + 'Z'); return isNaN(x.getTime()) ? ttmm(ts) : `${String(x.getDate()).padStart(2, '0')}.${String(x.getMonth() + 1).padStart(2, '0')}.`; };
// Vom System gesetzte Gründe stehen schon in der Quellenzeile
const systemGrund = (g: string) => g === 'Krankmeldung per Mail' || g === 'Ausflug nicht teilgenommen' || /^Stunde \d+$/.test(g);
const wtTtmm = (iso: string) => `${WT[d(iso).getDay()]} ${ttmm(iso)}`;
const ganzeTage = (e: { tage: number | null }) => (e.tage ?? 0) >= 1 && Number.isInteger(e.tage);
function umfangText(e: { stunden: number | null; tage: number | null }) {
  const std = e.stunden ?? 0;
  if (ganzeTage(e)) return `${e.tage} ${e.tage === 1 ? 'Tag' : 'Tage'} · ${std} Std.`;
  return `${std} Std.`;
}
function quelleZeile(e: FzEintrag): { icon: string; text: string } {
  switch (e.quelle) {
    case 'klassenbuch': return { icon: 'menu_book', text: e.kb_stunde ? `Klassenbuch · ${e.kb_stunde}. Stunde` : 'Klassenbuch' };
    case 'ausflug':     return { icon: 'directions_bus', text: e.ausflug_titel ?? 'Ausflug' };
    case 'mail':        return { icon: 'mail', text: 'Krankmeldung per Mail' };
    case 'diktat':      return { icon: 'mic', text: 'Per Diktat erfasst' };
    case 'manuell':     return { icon: 'edit', text: 'Von dir eingetragen' };
    default:            return { icon: 'help', text: 'Quelle unbekannt (älterer Eintrag)' };
  }
}
function aenderungText(e: FzEintrag): string | null {
  const a = e.letzte_aenderung; if (!a) return null;
  const am = ttmmUtc(a.am);
  if (a.nachberechnung) return `nachberechnet ${am} · ganzer Tag in Stunden`;
  const v = a.vorher;
  if ('entschuldigt' in v || 'attest' in v) {
    const alt = statusVon({ entschuldigt: v.entschuldigt ?? e.entschuldigt, attest: v.attest ?? e.attest });
    return `geändert ${am} · vorher ${STATUS_LABEL[alt].toLowerCase()}`;
  }
  if ('stunden' in v) return `geändert ${am} · vorher ${v.stunden ?? 0} Std.`;
  if ('datum' in v && v.datum) return `geändert ${am} · vorher ${ttmm(v.datum)}`;
  if ('tage' in v) return `geändert ${am}`;
  return null;
}

export function FehlzeitenEintraege({ schuelerId, schuelerName, attestOffen, attestSeit, neuLadenKey, onGeaendert, onEintragen, onVerlauf, attestBereich }: {
  schuelerId: number; schuelerName: string;
  attestOffen: number; attestSeit: string | null;
  neuLadenKey: number;
  onGeaendert: () => void;          // Profil: Note-Vorschläge etc. neu laden
  onEintragen?: () => void;         // optional: "+ Eintragen" (im Schülerprofil steht er seit P1d an fester Stelle)
  onVerlauf: () => void;            // bestehender Verlauf (audit_log)
  attestBereich: React.ReactNode;   // bestehende Attestpflicht-Karte
}) {
  const [eintraege, setEintraege] = useState<FzEintrag[] | null>(null);
  const [filter, setFilter] = useState<Filter>('alle');
  const [stundenProTag, setStundenProTag] = useState(6);
  const [edit, setEdit] = useState<FzEintrag | null>(null);
  const grenzen = ladeFehlzeitenGrenzen();
  const sj = schuljahrFenster();

  const lade = useCallback(async () => {
    try {
      const rows = await apiFetch<FzEintrag[]>(`/api/fehlzeiten?schueler_id=${schuelerId}`);
      setEintraege(rows.filter(r => r.datum >= sj.von && r.datum < sj.bisExkl));
    } catch (err) { zeigeApiFehler(err, 'Fehlzeiten konnten nicht geladen werden'); setEintraege([]); }
  }, [schuelerId, sj.von, sj.bisExkl]);
  useEffect(() => { lade(); }, [lade, neuLadenKey]);
  useEffect(() => {
    apiFetch<{ stundenProTag?: number }>('/api/lehrer/einstellungen')
      .then(e => { if (e?.stundenProTag) setStundenProTag(e.stundenProTag); }).catch(() => {});
  }, []);

  // Nach jeder Änderung: Liste + Profil-Summen/Ampel/Badges (SchuelerContext) neu laden.
  // Heute (§68-Zeile, Radar) lädt beim nächsten Öffnen ohnehin frisch.
  const nachAenderung = useCallback(async () => {
    await lade();
    window.dispatchEvent(new Event('lehrerdesk-schueler-reload'));
    onGeaendert();
  }, [lade, onGeaendert]);

  // Welle U6a: gemeinsame Note-6-Folgefrage (auch vom Klassenbuch genutzt)
  const note6Rueckfrage = (liste: BestaetigteNote6[] | undefined) => note6RueckfrageGemeinsam(liste, onGeaendert);

  async function put(e: FzEintrag, body: Record<string, unknown>) {
    return apiFetch<{ fehlzeit: FzEintrag; bestaetigte_note6: BestaetigteNote6[] }>(`/api/fehlzeiten/${e.id}`, { method: 'PUT', body: JSON.stringify(body) });
  }

  // Schnellknopf: sofort speichern, Toast mit Rückgängig (stellt den alten Stand per PUT her)
  async function attestNachgereicht(e: FzEintrag) {
    try {
      const r = await put(e, { attest: 1 });
      await nachAenderung();
      toast(`${wtTtmm(e.datum)} mit Attest entschuldigt`, 'success', {
        dauerMs: 5000,
        aktion: { label: 'Rückgängig', onClick: async () => {
          try { await put(e, { entschuldigt: e.entschuldigt, attest: e.attest }); await nachAenderung(); toast('Rückgängig gemacht', 'info'); }
          catch (err) { zeigeApiFehler(err, 'Rückgängig fehlgeschlagen'); }
        } },
      });
      await note6Rueckfrage(r.bestaetigte_note6);
    } catch (err) { zeigeApiFehler(err, 'Speichern fehlgeschlagen'); }
  }

  const liste = eintraege ?? [];
  const zahl = (s: Filter) => s === 'alle' ? liste.length : liste.filter(e => statusVon(e) === s).length;
  const sichtbar = filter === 'alle' ? liste : liste.filter(e => statusVon(e) === filter);
  const gruppen = useMemo(() => {
    const m = new Map<string, FzEintrag[]>();
    for (const e of sichtbar) { const k = e.datum.slice(0, 7); if (!m.has(k)) m.set(k, []); m.get(k)!.push(e); }
    return Array.from(m.entries());
  }, [sichtbar]);

  // ── Kennzahlen (alle in Stunden) ──
  const unen = liste.filter(e => !e.entschuldigt).reduce((n, e) => n + (e.stunden ?? 0), 0);
  const entsch = liste.filter(e => e.entschuldigt).reduce((n, e) => n + (e.stunden ?? 0), 0);
  const entschTage = liste.filter(e => e.entschuldigt && ganzeTage(e)).reduce((n, e) => n + (e.tage ?? 0), 0);
  const mitAttest = liste.filter(e => e.attest);
  const attestStd = mitAttest.reduce((n, e) => n + (e.stunden ?? 0), 0);
  const stufe = unen >= grenzen.kritisch ? 'rot' : unen >= grenzen.achtung ? 'amber' : 'ok';
  const skala = Math.max(Math.ceil(grenzen.kritisch * 1.5), unen);
  const pct = (v: number) => `${Math.min(100, (v / skala) * 100)}%`;

  return (
    <div className="ld-fzl">
      <div className="ld-fzl-kpis">
        <div id="ld-profil-68" className={`ld-fzl-kpi ld-fzl-kpi--unen ld-fzl-kpi--${stufe}`} style={{ scrollMarginTop: 16 }}>
          <div className="ld-fzl-kpi__label">Unentschuldigt · Schuljahr {sj.label}</div>
          <div className="ld-fzl-kpi__wert"><b>{unen}</b> Std.</div>
          <div className="ld-fzl-ampel" role="img" aria-label={`${unen} von ${grenzen.kritisch} Stunden bis §68`}>
            <div className="ld-fzl-ampel__fill" style={{ width: pct(unen) }} />
            <span className="ld-fzl-ampel__marke" style={{ left: pct(grenzen.achtung) }} />
            <span className="ld-fzl-ampel__marke ld-fzl-ampel__marke--rot" style={{ left: pct(grenzen.kritisch) }} />
          </div>
          <div className="ld-fzl-ampel__skala">
            <span>0</span><span style={{ left: pct(grenzen.achtung) }}>Achtung ab {grenzen.achtung}</span>
            <span style={{ left: pct(grenzen.kritisch) }}>§68 ab {grenzen.kritisch}</span>
          </div>
        </div>
        <div className="ld-fzl-kpi">
          <div className="ld-fzl-kpi__label">Entschuldigt</div>
          <div className="ld-fzl-kpi__wert"><b>{entsch}</b> Std.</div>
          {entschTage > 0 && <div className="ld-fzl-kpi__sub">davon {entschTage} {entschTage === 1 ? 'ganzer Tag' : 'ganze Tage'}</div>}
        </div>
        <div className="ld-fzl-kpi">
          <div className="ld-fzl-kpi__label">Davon mit Attest</div>
          <div className="ld-fzl-kpi__wert"><b>{attestStd}</b> Std.</div>
          <div className="ld-fzl-kpi__sub">{mitAttest.length} {mitAttest.length === 1 ? 'Eintrag' : 'Einträge'}</div>
        </div>
      </div>

      {attestOffen > 0 && (
        <div className="ld-fzl-attesthinweis" role="status">
          <MIcon name="medical_information" size={18} />
          <span><b>{attestOffen} {attestOffen === 1 ? 'Fehlzeit' : 'Fehlzeiten'} ohne Attest</b> seit {ttmm(attestSeit && attestSeit.slice(0, 10) > sj.von ? attestSeit : sj.von)}</span>
        </div>
      )}
      {attestBereich}

      <div className="ld-fzl-karte">
        <div className="ld-fzl-kopf">
          <div className="ld-fzl-kopf__titel">
            <h3>Einträge</h3>
            <span>Schuljahr {sj.label} · neueste zuerst</span>
          </div>
          <button className="ld-fzl-linkbtn" onClick={onVerlauf}><MIcon name="history" size={16} />Verlauf</button>
          {onEintragen && <button className="ld-fzl-neu" onClick={onEintragen}><MIcon name="add" size={18} />Fehlzeit eintragen</button>}
        </div>

        <div className="ld-fzl-filter" role="group" aria-label="Fehlzeiten filtern">
          {(['alle', 'unentsch', 'entsch', 'attest'] as Filter[]).map(f => (
            <button key={f} aria-pressed={filter === f} onClick={() => setFilter(f)}
              className={`ld-fzl-filter__pill${filter === f ? ' ld-fzl-filter__pill--aktiv' : ''}`}>
              {f === 'attest' && <MIcon name="medical_information" size={15} />}
              {f === 'alle' ? 'Alle' : f === 'attest' ? 'Mit Attest' : STATUS_LABEL[f]}
              <span className="ld-fzl-filter__zahl">{zahl(f)}</span>
            </button>
          ))}
        </div>

        {eintraege === null ? (
          <div className="ld-fzl-leer">Lade Fehlzeiten …</div>
        ) : sichtbar.length === 0 ? (
          <div className="ld-fzl-leer">
            {liste.length === 0 ? 'Im laufenden Schuljahr sind keine Fehlzeiten eingetragen.' : 'Keine Einträge in diesem Filter.'}
          </div>
        ) : gruppen.map(([monat, rows]) => (
          <div key={monat}>
            <div className="ld-fzl-monat">{MONATE[Number(monat.slice(5, 7)) - 1]} {monat.slice(0, 4)}</div>
            {rows.map(e => {
              const st = statusVon(e); const q = quelleZeile(e); const geaendert = aenderungText(e);
              return (
                <div key={e.id} className="ld-fzl-zeile">
                  <button className="ld-fzl-zeile__haupt" onClick={() => e.bearbeitbar && setEdit(e)} disabled={!e.bearbeitbar}
                    aria-label={`Fehlzeit vom ${wtTtmm(e.datum)} bearbeiten`}>
                    <span className="ld-fzl-datum">
                      <span className="ld-fzl-datum__wt">{WT[d(e.datum).getDay()]}</span>
                      <span className="ld-fzl-datum__tag">{d(e.datum).getDate()}</span>
                      <span className="ld-fzl-datum__mon">{MON_KURZ[d(e.datum).getMonth()]}</span>
                    </span>
                    <span className="ld-fzl-zeile__text">
                      <span className="ld-fzl-zeile__umfang">{umfangText(e)}{e.kb_fach && <span className="ld-fzl-zeile__fach"> · {e.kb_fach}</span>}
                        {e.grund && !systemGrund(e.grund) && <span className="ld-fzl-zeile__grund"> · „{e.grund}“</span>}</span>
                      <span className="ld-fzl-zeile__quelle">
                        <span className="ld-fzl-zeile__q"><MIcon name={q.icon} size={14} />{q.text}</span>
                        {geaendert && <span className="ld-fzl-zeile__geaendert"><MIcon name="history" size={14} />{geaendert}</span>}
                      </span>
                    </span>
                  </button>
                  <span className="ld-fzl-zeile__aktionen">
                    {st === 'unentsch' && e.bearbeitbar && (
                      <button className="ld-fzl-attestbtn" onClick={() => attestNachgereicht(e)} title="Attest nachgereicht" aria-label={`Attest nachgereicht für ${wtTtmm(e.datum)}`}>
                        <MIcon name="medical_information" size={16} /><span className="ld-fzl-attestbtn__text">Attest nachgereicht</span>
                      </button>
                    )}
                    <span className={`ld-fzl-pill ld-fzl-pill--${st}`}>
                      {st !== 'unentsch' && <MIcon name={STATUS_ICON[st]} size={14} />}
                      <span className="ld-fzl-pill__lang">{st === 'attest' ? 'Attest' : STATUS_LABEL[st]}</span>
                      <span className="ld-fzl-pill__kurz">{STATUS_KURZ[st]}</span>
                    </span>
                    {e.bearbeitbar && (
                      <button className="ld-fzl-editbtn" onClick={() => setEdit(e)} aria-label={`Fehlzeit vom ${wtTtmm(e.datum)} bearbeiten`}>
                        <MIcon name="edit" size={18} />
                      </button>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {edit && (
        <FehlzeitBearbeitenSheet
          eintrag={edit} schuelerName={schuelerName} stundenProTag={stundenProTag} sjVon={sj.von} sjBisExkl={sj.bisExkl}
          onClose={() => setEdit(null)}
          onWiederOeffnen={(e) => setEdit(e)}
          onGespeichert={async (r) => { setEdit(null); await nachAenderung(); toast('Fehlzeit gespeichert', 'success'); await note6Rueckfrage(r?.bestaetigte_note6); }}
          onGeloescht={async (r) => { await nachAenderung(); toast(r.hinweis ? `Fehlzeit gelöscht · ${r.hinweis}` : 'Fehlzeit gelöscht', 'success', { dauerMs: r.hinweis ? 4000 : 2000 }); await note6Rueckfrage(r.bestaetigte_note6); }}
        />
      )}
    </div>
  );
}

// ── Bearbeiten-Sheet ─────────────────────────────────────────────────────────
function FehlzeitBearbeitenSheet({ eintrag: e, schuelerName, stundenProTag, sjVon, sjBisExkl, onClose, onWiederOeffnen, onGespeichert, onGeloescht }: {
  eintrag: FzEintrag; schuelerName: string; stundenProTag: number; sjVon: string; sjBisExkl: string;
  onClose: () => void;
  onWiederOeffnen: (e: FzEintrag) => void;
  onGespeichert: (r: { bestaetigte_note6: BestaetigteNote6[] } | null) => void;
  onGeloescht: (r: { hinweis: string | null; bestaetigte_note6: BestaetigteNote6[] }) => void;
}) {
  const gesperrt = e.gesperrte_felder.length > 0;
  const [datum, setDatum] = useState(e.datum.slice(0, 10));
  const [modus, setModus] = useState<'stunden' | 'tag'>(ganzeTage(e) ? 'tag' : 'stunden');
  const [stunden, setStunden] = useState(Math.max(1, e.stunden ?? 1));
  const [tage, setTage] = useState(ganzeTage(e) ? (e.tage as number) : 1);
  const [status, setStatus] = useState<Status>(statusVon(e));
  const [grund, setGrund] = useState(e.grund ?? '');
  const [laeuft, setLaeuft] = useState(false);
  const herkunft = e.quelle === 'ausflug' ? 'dem Ausflug' : 'dem Klassenbuch';

  async function speichern() {
    if (!gesperrt && !(datum >= sjVon && datum < sjBisExkl)) { toast('Das Datum muss im laufenden Schuljahr liegen.', 'error'); return; }
    const body: Record<string, unknown> = {
      entschuldigt: status === 'unentsch' ? 0 : 1, attest: status === 'attest' ? 1 : 0, grund: grund.trim() || null,
    };
    if (!gesperrt) {
      body.datum = datum;
      if (modus === 'tag') { body.tage = tage; body.stunden = null; } else { body.stunden = stunden; body.tage = null; }
    }
    setLaeuft(true);
    try {
      const r = await apiFetch<{ bestaetigte_note6: BestaetigteNote6[] }>(`/api/fehlzeiten/${e.id}`, { method: 'PUT', body: JSON.stringify(body) });
      onGespeichert(r);
    } catch (err) { zeigeApiFehler(err, 'Speichern fehlgeschlagen'); }
    finally { setLaeuft(false); }
  }

  async function loeschen() {
    // Bestätigung erst nach dem Schließen des Sheets (modales Sheet blockiert sonst den Dialog)
    onClose();
    const ja = await confirm(e.loeschen_hinweis ?? `Fehlzeit vom ${ttmm(e.datum)} endgültig löschen?`, {
      title: e.loeschen_hinweis ? `Fehlzeit vom ${ttmm(e.datum)} löschen?` : 'Fehlzeit löschen?', confirmLabel: 'Löschen', danger: true,
    });
    if (!ja) { onWiederOeffnen(e); return; }
    try {
      const r = await apiFetch<{ hinweis: string | null; bestaetigte_note6: BestaetigteNote6[] }>(`/api/fehlzeiten/${e.id}`, { method: 'DELETE' });
      onGeloescht(r);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) toast(err.message, 'info'); else zeigeApiFehler(err, 'Löschen fehlgeschlagen');
    }
  }

  function quelleOeffnen() {
    onClose();
    // Welle U6b: Zurück führt ins Profil (Tab Fehlzeiten); Klassenbuch öffnet am Datum + Stunde
    const herkunft = { profilId: e.schueler_id, ziel: 'fehlzeiten' as const };
    if (e.quelle === 'ausflug' && e.ausflug_termin_id) openAusflugDetail(e.ausflug_termin_id, herkunft);
    else oeffneKlassenbuch({ datum: e.datum.slice(0, 10), stunde: e.kb_stunde, schuelerId: e.schueler_id, herkunft });
  }

  const datumLang = d(e.datum).toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' });
  return (
    <LdSheet open onClose={onClose} maxWidth={520}
      kopf={{ titel: 'Fehlzeit bearbeiten', kontext: `${schuelerName}, ${wtTtmm(e.datum)}${e.quelle === 'manuell' ? ', von dir eingetragen' : ''}` }}
      fuss={<LdFensterFuss onAbbrechen={onClose} haupt="Speichern" onHaupt={speichern} laden={laeuft}
        links={<button type="button" className="ld-kl-link" style={{ color: 'var(--ld-danger-text)' }} onClick={loeschen}>Löschen</button>} />}>
      {gesperrt ? (
        <LdFeld label="Datum und Umfang">
          <div className="ld-fzl-gesperrt">
            <span>{datumLang}, {umfangText(e)}{e.kb_stunde ? `, ${e.kb_stunde}. Stunde` : ''}{e.kb_fach ? `, ${e.kb_fach}` : ''}</span>
            <MIcon name="lock" size={16} />
          </div>
          <div className="ld-fzl-quellhinweis">
            <MIcon name={e.quelle === 'ausflug' ? 'directions_bus' : 'menu_book'} size={18} />
            <span>Kommt aus {herkunft}. Datum und Umfang änderst du dort.{' '}
              <button className="ld-fzl-inlinelink" onClick={quelleOeffnen}>{e.quelle === 'ausflug' ? 'Ausflug öffnen' : 'Klassenbuch öffnen'}</button>
            </span>
          </div>
        </LdFeld>
      ) : (
        <>
          <LdFeld label="Datum">
            <LdDatum aria-label="Datum" required value={datum} min={sjVon} onChange={ev => setDatum(ev.target.value)} />
          </LdFeld>
          <LdFeld label="Umfang" hinweis={modus === 'tag' ? `${tage} ${tage === 1 ? 'Tag' : 'Tage'} = ${tage * stundenProTag} Std. (ein ganzer Tag zählt ${stundenProTag} Std.)` : undefined}>
            <div className="ld-fzl-umfang">
              <LdUmschalter aria-label="Umfang" wert={modus} onChange={setModus}
                optionen={[{ wert: 'stunden', label: 'Stunden' }, { wert: 'tag', label: 'Ganzer Tag' }]} />
              {modus === 'tag'
                ? <LdZahl aria-label="Tage" value={tage} min={1} max={10} onChange={setTage} />
                : <LdZahl aria-label="Stunden" value={stunden} min={1} max={12} onChange={setStunden} />}
            </div>
          </LdFeld>
        </>
      )}

      <LdFeld label="Status" hinweis={e.quelle === 'klassenbuch' && e.klassenbuch_id && status !== 'unentsch' ? 'Wird auch im Klassenbuch als entschuldigt vermerkt.' : undefined}>
        <LdUmschalter aria-label="Status" wert={status} onChange={setStatus}
          optionen={(['unentsch', 'entsch', 'attest'] as Status[]).map(s => ({ wert: s, label: STATUS_LABEL[s], ton: s === 'unentsch' ? 'amber' as const : 'gruen' as const }))} />
      </LdFeld>

      <LdFeld label="Grund oder Notiz" optional htmlFor="ld-fzl-grund">
        <LdTextfeld id="ld-fzl-grund" rows={2} value={grund} onChange={ev => setGrund(ev.target.value)} placeholder="z. B. Attest vom Hausarzt liegt vor" />
      </LdFeld>
    </LdSheet>
  );
}
